import React, { useEffect, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import MealOptionsTable from "./Components/MealOptionsTable";
import CircularProgress from "@mui/material/CircularProgress";
import {
  Alert,
  AlertTitle,
  Backdrop,
  Box,
  Button,
  Card,
  Typography,
} from "@mui/material";
import { useMealOptionsStore } from "./stores/mealOptionsStore";
import { AxiosError } from "axios";
import Translate from "../../react-shared/Translations";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ReplayIcon from "@mui/icons-material/Replay";
import OrderUpdateConfirmationModal from "./Components/OrderUpdateConfirmationModal";
import { useHeaderCheckboxSync } from "./hooks/useHeaderCheckboxSync";
import { useOrderUpdate } from "./hooks/useOrderUpdate";

interface Props {
  pageState: PageState;
}

type PageState = {
  ClientId: string;
  CategoryId: string;
  IsLocked: boolean;
  CanUnlockOrders: boolean;
};

export const feature_js_client_diet_settings = (el: string) => {
  var $el = $(el);

  const pageState: PageState = {
    ClientId: $el.attr("ClientId")!,
    CategoryId: $el.attr("CategoryId")!,
    IsLocked: !Boolean($el.attr("IsUnLocked")),
    CanUnlockOrders: Boolean($el.attr("CanUnlockOrders") === "True"),
  };

  var element = document.getElementById("client-diet-settings");

  var element = document.getElementById("client-diet-settings");

  // Create a client
  const queryClient = new QueryClient();
  ReactDOM.render(
    <div>
      <QueryClientProvider client={queryClient}>
        <ClientDietSettings pageState={pageState} />
      </QueryClientProvider>
    </div>,
    element
  );
};

export const ClientDietSettings = ({ pageState }: Props) => {
  const [isLoadingHook, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [exceptionMessage, setException] = useState("");

  const {
    isModified,
    setAllMealOptionShow,
    setAllMealOptionTobeOrdered,
    setAllMealOptionFoodFree,
    initializeState,
    removeModifiedState,
    mealOptions,
  } = useMealOptionsStore();

  // Use custom hooks
  const { showHeaderChecked, toBeOrderedHeaderChecked, foodFreeHeaderChecked } =
    useHeaderCheckboxSync(mealOptions);

  const {
    isLoading: isOrderUpdateLoading,
    showModal,
    affectedOrders,
    saveAndCheckOrders,
    confirmOrderUpdate,
    cancelOrderUpdate,
  } = useOrderUpdate({
    clientId: pageState.ClientId,
    onSuccess: removeModifiedState,
    onError: (error) => {
      setHasError(true);
      setException(error.message);
    },
  });

  const getMealOptions = useCallback(async () => {
    try {
      await initializeState(pageState.ClientId, pageState.CategoryId);
      setIsLoading(false);
    } catch (err) {
      console.error(err);
      setHasError(true);
      setException((err as AxiosError).message);
      setIsLoading(false);
    }
  }, [pageState.ClientId, pageState.CategoryId, initializeState]);

  useEffect(() => {
    setIsLoading(true);
    getMealOptions();
  }, [getMealOptions]);

  return (
    <>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: 99,
        }}
        open={isModified}
      ></Backdrop>

      <Card
        sx={{
          padding: "5px 5px 15px 18px",
          position: "relative",
          zIndex: 100,
        }}
      >
        {isLoadingHook ? (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress size={32} />
          </Box>
        ) : hasError ? (
          <Alert variant="outlined" severity="error">
            <AlertTitle>
              <Typography variant="body2" fontWeight="bold">
                {Translate("Server_Error")}
              </Typography>
            </AlertTitle>
            <Typography variant="body2">{exceptionMessage}</Typography>
          </Alert>
        ) : (
          <MealOptionsTable
            onShowHeaderClick={setAllMealOptionShow}
            showHeaderChecked={showHeaderChecked}
            onToBeOrderedHeaderClick={setAllMealOptionTobeOrdered}
            toBeOrderedHeaderChecked={toBeOrderedHeaderChecked}
            onFoodFreeHeaderClick={setAllMealOptionFoodFree}
            foodFreeHeaderChecked={foodFreeHeaderChecked}
            isLocked={Boolean(pageState.IsLocked)}
          />
        )}
        <LoadingButton
          hidden={!isModified}
          size="small"
          endIcon={<SaveIcon fontSize="small" />}
          loading={isOrderUpdateLoading}
          loadingPosition="end"
          onClick={saveAndCheckOrders}
          sx={{
            marginRight: 2,
            marginTop: 2,
            textTransform: "unset",
            visibility: !isModified ? "hidden" : "unset",
          }}
          variant="contained"
        >
          {Translate("Save")}
        </LoadingButton>
        <Button
          size="small"
          endIcon={<ReplayIcon fontSize="small" />}
          sx={{
            visibility: !isModified ? "hidden" : "unset",
            marginRight: 2,
            marginTop: 2,
            textTransform: "unset",
          }}
          onClick={getMealOptions}
          variant="contained"
        >
          {Translate("Undo")}
        </Button>
      </Card>

      <OrderUpdateConfirmationModal
        open={showModal}
        orders={affectedOrders}
        mealOptions={mealOptions}
        canUnlockOrders={pageState.CanUnlockOrders}
        onConfirm={confirmOrderUpdate}
        onCancel={cancelOrderUpdate}
      />
    </>
  );
};
